<template>
  <div class="ws-select ws-field wrapper">
    <slot
      name="custom-display"
      :selected-value="displayValue"
      :open-dropdown="openDropdown"
      :raw-selected-value="selectedValue"
    ></slot>
    <q-select
      ref="qfield"
      v-bind="$attrs"
      :name="name"
      :emit-value="emitValue"
      :value="selectedValue"
      :options="filteredOptions"
      class="ws-select-inner"
      :display-value="displayValue"
      :class="{
        'ws-select-chips': $attrs['use-chips'] === '',
        'ws-select-empty': value === '' || get(value, 'length') === 0,
        'ws-dark': dark,
        'q-field--error': errorDesign,
        'messages-overflow': messagesOverflow,
        'custom-display': isCustomDisplay
      }"
      :popup-content-class="
        ($attrs['popup-content-class'] || '') + ' ws-select-content wsk-root wsk-app wsk-portal ws-typography'
          + (widePaddings ? ' wide-paddings' : '')
          + (optionsWithList ? ' options-with-list' : '')
      "
      :bg-color="bgColor || (dark ? 'dark' : 'white')"
      :standout="dark ? 'bg-dark text-white' : 'bg-white ws-text-darker'"
      :dark="dark"
      :placeholder="getPlaceholder"
      :square="isTheme(Theme.ADMIN)"
      :multiple="multiple"
      outlined
      :use-chips="multiple"
      no-error-icon
      :use-input="allowCustomValue || reduceOptionsByQuery || withInput || useInput"
      :fill-input="allowCustomValue && !multiple"
      :hide-selected="allowCustomValue && !multiple"
      :input-debounce="reduceOptionsByQuery ? 300 : undefined"
      lazy-rules
      transition-show="jump-down"
      transition-hide="jump-up"
      bottom-slots
      :error="!!messages.length"
      :error-message="inputErrorMessage"
      behavior="menu"
      hide-dropdown-icon
      @filter="filterOptions"
      v-on="$listeners"
      @new-value="createValue"
      @focus="onFocus"
      @focusout.native="onFocusOut"
      @input="onInput"
      @input-value="onInputValueChange"
      @keydown.enter="handleEnter"
    >
      <template v-if="icon" v-slot:append>
        <ws-icon :name="icon" style="height: 100%" />
      </template>

      <template v-if="optionsWithList" v-slot:selected-item="{opt}">
        <ws-select-option-with-list
          class="q-mb-sm"
          :border="false"
          :option="opt"
        ></ws-select-option-with-list>
      </template>

      <template v-if="multiple" v-slot:before-options>
        <div class="q-mx-md q-py-md" style="border-bottom: 1px solid #BEC8D1">
          <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
          <!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
          <div class="q-item cursor-pointer flex flex-row no-margin" @click="selectAll">
            <ws-checkbox :value="allSelected" class="q-mr-md"></ws-checkbox>
            <span>{{ $t('uikit:ws_table.label.select_all') }}</span>
          </div>
          <!-- eslint-enable vuejs-accessibility/no-static-element-interactions -->
          <!-- eslint-enable vuejs-accessibility/click-events-have-key-events -->
        </div>
      </template>

      <!-- credit: https://stackoverflow.com/a/50892881/861615
        $slots and $scopedSlots cannot be merged because:
        https://stackoverflow.com/questions/64099203/#comment118694097_64099515
      -->

      <template v-slot:option="{ itemProps, opt, selected, toggleOption }">
        <q-item v-bind="itemProps" class="q-pa-5xl" @click.capture.stop="toggleOption(opt)">
          <template v-if="optionsWithList">
            <q-item-section>
              <ws-select-option-with-list
                :option="opt"
              ></ws-select-option-with-list>
            </q-item-section>
          </template>
          <template v-else>
            <q-item-section v-if="multiple" side class="q-mr-sm">
              <ws-checkbox :value="selected"></ws-checkbox>
            </q-item-section>
            <q-item-section v-if="opt.icon" side>
              <ws-icon :name="opt.icon" :height="16"></ws-icon>
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ typeof opt === 'string' ? opt : opt.label }}</q-item-label>
            </q-item-section>
          </template>
        </q-item>
      </template>

      <template v-for="(_, slot) of slots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope"></slot>
      </template>

      <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="{ ...scope, emphasiseQuery }"></slot>
      </template>
    </q-select>
  </div>
</template>

<script lang="ts">
import { Component, Inject, Prop, Watch } from 'vue-property-decorator';
import {QSelect} from 'quasar';
import {mixins} from 'vue-class-component';
import {emphasiseQuery} from '@odin/utils';
import {escape, get} from 'lodash-es';
import {WsFieldMixin} from './WsFieldMixin.vue';
import { WsIcon, WsSelectOptionWithList } from '@odin/components-vue2';
import { Theme } from '@odin/services';
import type {Options, OptionsWithLabel} from './WsSelect';
import WsCheckbox from './WsCheckbox.vue';

@Component({
  components: {
    WsSelectOptionWithList,
    QSelect,
    WsIcon,
    WsCheckbox,
  },
})
export class WsSelect extends mixins(WsFieldMixin) {
  @Prop({default: null}) declare readonly value: string | number;
  @Prop({}) readonly options!: Options | OptionsWithLabel;
  @Prop(Boolean) readonly allowCustomValue!: boolean;
  @Prop(Boolean) readonly withInput!: boolean;
  @Prop(Boolean) readonly useInput!: boolean;
  @Prop(Boolean) readonly reduceOptionsByQuery!: boolean;
  @Prop(Boolean) readonly openOptionsOnFocus!: boolean;
  @Prop(Boolean) readonly dark!: boolean;
  @Prop(Boolean) readonly errorDesign!: boolean;
  @Prop(Boolean) readonly multiple!: boolean;
  @Prop(Boolean) readonly optionsWithList!: boolean;
  @Prop(String) readonly bgColor!: string;
  @Prop(String) readonly placeholder!: string;
  @Prop({default: false}) declare readonly messagesOverflow: boolean;
  @Prop({type: String, default: 'arrow_down'}) readonly icon!: string;
  @Prop({type: String, default: ''}) readonly name!: string;
  @Prop({type: Boolean, default: false}) readonly emitValue!: string;
  @Prop({type: Boolean, default: false}) readonly widePaddings!: boolean;

  @Inject({from: 'formDataOverride', default: null}) formDataOverride: any;

  selectedValue: any = null;
  query: string | null | number = null;
  cachedOptions: string[] | null = null;
  filteredOptions: any[] = [];
  timeoutRef: NodeJS.Timeout | null = null;
  Theme = Theme;

  mounted() {
    this.filteredOptions = this.options;
  }

  public get qField(): QSelect {
    return get(this, '$refs.qfield', {});
  }

  public get optionIndex() {
    return (this.qField as any) /* optionIndex is not public!!! */.optionIndex;
  }

  handleEnter() {
    if (this.allowCustomValue && this.multiple && this.query !== '') {
      this.qField.setOptionIndex(-1);
    }

    if (this.allowCustomValue) {
      this.sendValue();
    }
  }

  public get getPlaceholder() {
    return this.selectedValue ? null : this.placeholder;
  }

  public filter(query: string) {
    this.query = query;
    (this.qField as QSelect).filter(query);
  }

  get displayValue() {
    try {
      return (this.options as OptionsWithLabel)
        .find(o => o.value?.toString() ===
          (this.selectedValue?.value?.toString() || this.selectedValue?.toString()))?.label;
    } catch (e) {
      return this.selectedValue?.label || this.selectedValue?.value?.toString() || this.selectedValue;
    }
  }

  @Watch('value', {immediate: true})
  onValueChanged(newVal: string) {
    if (this.emitValue && this.formDataOverride) {
      this.formDataOverride[this.name] = newVal;
    }

    // Sometimes values come as JSON strings
    try {
      newVal = JSON.parse(newVal);
    } catch {
    } finally {
      this.selectedValue = newVal;
      this.$emit('change', this.selectedValue);
    }
  }

  @Watch('options', {immediate: true})
  onOptionsChanged() {

    // Clear the current value if it is not in the new options.
    if (!this.allowCustomValue && !this.multiple) {
      const value = this.selectedValue?.value || this.selectedValue;

      if (value) {
        const values = this.options.map((option: any) => option.value || option);

        if (!values.includes(value)) {
          this.selectedValue = null;
        }
      }
    }

    this.updateOptions();
  }

  openDropdown() {
    this.qField.showPopup();
  }

  closeDropdown() {
    this.qField.hidePopup();
  }

  get isCustomDisplay() {
    return !!this.$scopedSlots['custom-display'];
  }

  onInput(value: any) {
    this.selectedValue = value;
    this.$emit('input', value);
  }

  // eslint-disable-next-line no-unused-vars
  filterOptions(val: string, update: (fn: () => void) => void) {
    update(() => this.updateOptions(val));
  }

  updateOptions(query = this.query?.toString() || '') {
    if (query !== '' && this.reduceOptionsByQuery) {

      query = query.toLowerCase();

      // @ts-ignore
      this.filteredOptions = this.options?.filter((option: any) => {
        if (typeof option === 'string') {
          return option.toLowerCase().includes(query);
        } else if (option?.label) {
          return option.label.toLowerCase().includes(query);
        }
      });
    } else {
      this.filteredOptions = this.options;
    }
  }

  createValue(val: any, done: any) {
    if (!this.multiple) {
      return;
    }

    if (val.length > 0 && !this.filteredOptions.includes(val)) {
      done(val, 'add-unique');
    }
  }

  selectAll() {
    if (this.multiple) {
      if (this.allSelected) {
        this.selectedValue = [];
      } else {
        this.selectedValue = this.options;
      }
    }
  }

  get allSelected() {
    return this.selectedValue?.length === this.options.length;
  }

  /*
    emphasiseQuery - highlights matches of query string in option

    EXAMPLE USAGE

      <template v-slot:option="scope">
      <q-item
        class="ws-places-input-item"
        v-bind="scope.itemProps"
        v-on="scope.itemEvents"
      >
        <q-item-section
          v-html="
            scope.emphasiseQuery(scope.opt)
          "
        ></q-item-section>
      </q-item>
    </template>
  */
  public emphasiseQuery(option: string): string {
    if (!this.query) {
      return option;
    }
    return `<span>${emphasiseQuery(option, escape(this.query.toString().trim()))}</span>`;
  }

  onInputValueChange(value: string) {
    this.query = value;

    if (this.allowCustomValue && !this.multiple) {
      this.selectedValue = this.query;
    }
  }

  onFocus() {
    if (this.openOptionsOnFocus) {
      // seTimeout to wait for QSelect to be ready, otherwise it is flickering
      // ref: https://loopiagroup.atlassian.net/browse/PNO-870?focusedCommentId=140895
      this.timeoutRef = setTimeout(() => {
        this.qField?.showPopup?.();
      }, 300);
    }
  }

  onFocusOut() {
    this.sendValue();
  }

  sendValue() {
    if (this.selectedValue !== null && this.allowCustomValue) {
      this.$emit('input', this.selectedValue);
    }
  }

  destroyed() {
    clearTimeout(this.timeoutRef!);
  }
}

export default WsSelect;
</script>

<style lang="scss" scoped>
.ws-select {
  ::v-deep {
    .q-chip {
      gap: 10px;

      height: auto;
      margin-left: 0;
      margin-right: map-get($space-sm, x);
      padding: map-get($space-sm, y) 12px;

      border-radius: 50px;
      background-color: $ws-light-bg-neutral;

      line-height: 16px;
    }

    .q-field__append {
      height: 100% !important;
    }
  }
}
.ws-select-inner.messages-overflow::v-deep .q-field__bottom {
  height: 0;
  overflow-y: visible;
}

.ws-select-inner.custom-display {
  height: 0;
  overflow: hidden;
  width: 0;

  ::v-deep .q-field__inner .q-field__control {
    min-height: 0;
    overflow: hidden;
  }
}

.ws-select::v-deep .q-field__native > span {
  word-break: break-word;
}
</style>
<style lang="scss">
.ws-select-content {
   > .q-virtual-scroll__content > .q-item--active {
     color: $ws-blue;
   }

  &.wide-paddings {
    border-radius: 6px;

    > .q-virtual-scroll__content {
      padding: 16px 32px 24px 16px !important;

      > .q-item {
        height: 32px;
        margin: 0 0 4px;
        padding: 0 24px 0 16px;
      }
    }
  }

  &.options-with-list {
    min-width: 0 !important;
    max-width: 300px;
    border: 1px solid $neutral-message-border;
    border-radius: 6px;
    margin-top: 4px !important;
    box-shadow: 0 4px 32px 0 rgba($ws-navy-100, 0.15);

    &::-webkit-scrollbar {
      display: none;
    }

    > .q-virtual-scroll__content {
      padding: 16px 16px 0;
      width: 300px;

      > .q-item {
        margin: 0 0 16px;
        padding: 0;
      }
    }
  }
}
</style>
